/**
 * Deal Comp Analysis Email Template
 * Generates detailed investment analysis with comparable sales data
 */

export interface CompData {
  address?: string;
  unparsed_address?: string;
  close_price?: number;
  close_date?: string;
  listing_id?: string;
  living_area?: number;
  distance_miles?: number;
  price_per_sqft?: number;
  // Legacy field names for backward compatibility
  sale_price?: number;
  sold_price?: number;
  comp_sold_price?: number;
  sold_date?: string;
  comp_sold_date?: string;
  bedrooms?: number;
  comp_bedrooms?: number;
  bathrooms?: number;
  comp_bathrooms?: number;
  comp_living_area?: number;
  comp_distance_miles?: number;
  comp_price_per_sqft?: number;
}

export interface DealCompAnalysisData {
  propertyAddress: string;
  mls_id: string;
  shareLink: string;
  
  // Property details
  listPrice: number;
  bedrooms?: number;
  bathrooms?: number;
  sqft?: number;
  lotSize?: number;
  yearBuilt?: number;
  daysOnMarket?: number;
  propertyType?: string;
  
  // Financial metrics
  roi: number;
  arv: number;
  asIsValue?: number;
  renoEstimate: number;
  profit: number;
  mao20pct?: number;
  mao15pct?: number;
  maoGapPct?: number;
  investmentGrade?: string;
  
  // Comp data
  totalComps?: number;
  renovatedComps?: CompData[];
  asIsComps?: CompData[];
  
  // Agent info
  agentName?: string;
  agentPhone?: string;
  agentEmail?: string;
}

export function generateDetailedCompAnalysisTemplate(data: DealCompAnalysisData): string {
  const {
    propertyAddress,
    mls_id,
    shareLink,
    listPrice,
    bedrooms,
    bathrooms,
    sqft,
    lotSize,
    yearBuilt,
    daysOnMarket,
    roi,
    arv,
    asIsValue,
    renoEstimate,
    profit,
    mao20pct,
    mao15pct,
    maoGapPct,
    investmentGrade,
    totalComps,
    renovatedComps = [],
    asIsComps = [],
    agentName,
    agentPhone,
    agentEmail
  } = data;

  // Debug logging
  console.log('DealCompAnalysisData received:', {
    propertyAddress,
    totalComps,
    renovatedCompsLength: renovatedComps?.length || 0,
    asIsCompsLength: asIsComps?.length || 0,
    renovatedCompsData: renovatedComps?.slice(0, 2), // Show first 2 for debugging
    asIsCompsData: asIsComps?.slice(0, 2)
  });

  // Calculate conservative metrics
  const conservativeRenovation = 30000; // Standard $30k budget
  const conservativeARV = calculateConservativeARV(renovatedComps);
  const holdingCosts = Number(listPrice || 0) * 0.008 * 4; // 4 months
  const sellingCosts = conservativeARV * 0.06; // 6%
  const totalInvestment = Number(listPrice || 0) + conservativeRenovation + holdingCosts + sellingCosts;
  const conservativeProfit = conservativeARV - totalInvestment;
  const conservativeROI = totalInvestment > 0 ? (conservativeProfit / totalInvestment * 100) : 0;

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Detailed Investment Analysis - ${propertyAddress}</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; background-color: #f5f5f5; color: #333;">
  
  <!-- Wrapper -->
  <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #f5f5f5;">
    <tr>
      <td align="center" style="padding: 20px 0;">
        
        <!-- Main Container -->
        <table cellpadding="0" cellspacing="0" border="0" width="700" style="background-color: #ffffff; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
          
          <!-- Header -->
          <tr>
            <td style="background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%); padding: 40px 30px; text-align: center;">
              <h1 style="margin: 0; color: #ffffff; font-size: 28px; font-weight: 600;">
                📊 Detailed Investment Analysis
              </h1>
              <p style="margin: 10px 0 0; color: #e2e8f0; font-size: 18px;">
                Professional Comp Analysis Report
              </p>
            </td>
          </tr>
          
          <!-- Executive Summary -->
          <tr>
            <td style="padding: 40px 30px; border-bottom: 1px solid #e2e8f0;">
              <h2 style="margin: 0 0 20px; color: #2d3748; font-size: 24px;">
                ${propertyAddress}
              </h2>
              
              <div style="background: #f0fff4; border: 2px solid #68d391; border-radius: 8px; padding: 20px; margin-bottom: 20px;">
                <table width="100%" cellpadding="0" cellspacing="0">
                  <tr>
                    <td width="33%" align="center" style="border-right: 1px solid #68d391;">
                      <div style="color: #22543d; font-size: 14px; margin-bottom: 5px;">Investment Grade</div>
                      <div style="color: #22543d; font-size: 28px; font-weight: 700;">${investmentGrade || 'A'}</div>
                    </td>
                    <td width="33%" align="center" style="border-right: 1px solid #68d391;">
                      <div style="color: #22543d; font-size: 14px; margin-bottom: 5px;">ROI Potential</div>
                      <div style="color: #22543d; font-size: 28px; font-weight: 700;">${Number(roi || 0).toFixed(1)}%</div>
                    </td>
                    <td width="33%" align="center">
                      <div style="color: #22543d; font-size: 14px; margin-bottom: 5px;">Profit Potential</div>
                      <div style="color: #22543d; font-size: 28px; font-weight: 700;">$${Number(profit || 0).toLocaleString()}</div>
                    </td>
                  </tr>
                </table>
              </div>
              
              <table width="100%" cellpadding="12" cellspacing="0" style="background: #f7fafc; border-radius: 6px; margin-top: 20px;">
                <tr>
                  <td width="50%" style="padding: 15px; border-right: 1px solid #e2e8f0;">
                    <div style="margin-bottom: 8px;">
                      <strong style="color: #718096; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">MLS Number</strong><br>
                      <span style="color: #2d3748; font-size: 16px; font-weight: 600;">${mls_id}</span>
                    </div>
                  </td>
                  <td width="50%" style="padding: 15px;">
                    <div style="margin-bottom: 8px;">
                      <strong style="color: #718096; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Days on Market</strong><br>
                      <span style="color: #2d3748; font-size: 16px; font-weight: 600;">${daysOnMarket || 'New Listing'}</span>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td style="padding: 15px; border-right: 1px solid #e2e8f0; border-top: 1px solid #e2e8f0;">
                    <div>
                      <strong style="color: #718096; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Property Details</strong><br>
                      <span style="color: #2d3748; font-size: 16px; font-weight: 600;">
                        ${bedrooms || '?'} Beds • ${bathrooms || '?'} Baths • ${sqft?.toLocaleString() || '?'} sq ft
                      </span>
                    </div>
                  </td>
                  <td style="padding: 15px; border-top: 1px solid #e2e8f0;">
                    <div>
                      <strong style="color: #718096; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Lot Size</strong><br>
                      <span style="color: #2d3748; font-size: 16px; font-weight: 600;">${lotSize?.toLocaleString() || 'N/A'} sq ft</span>
                    </div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
          <!-- Financial Analysis -->
          <tr>
            <td style="padding: 40px 30px; border-bottom: 1px solid #e2e8f0;">
              <h3 style="margin: 0 0 20px; color: #2d3748; font-size: 22px;">
                💰 Financial Analysis
              </h3>
              
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td width="48%" valign="top">
                    <div style="background: #edf2f7; border-radius: 6px; padding: 20px; margin-bottom: 10px;">
                      <div style="color: #718096; font-size: 12px; margin-bottom: 5px;">LIST PRICE</div>
                      <div style="color: #2d3748; font-size: 24px; font-weight: 600;">$${Number(listPrice || 0).toLocaleString()}</div>
                    </div>
                  </td>
                  <td width="4%"></td>
                  <td width="48%" valign="top">
                    <div style="background: #edf2f7; border-radius: 6px; padding: 20px; margin-bottom: 10px;">
                      <div style="color: #718096; font-size: 12px; margin-bottom: 5px;">AFTER REPAIR VALUE</div>
                      <div style="color: #2d3748; font-size: 24px; font-weight: 600;">$${Number(arv || 0).toLocaleString()}</div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td valign="top">
                    <div style="background: #fef5e7; border-radius: 6px; padding: 20px; margin-bottom: 10px;">
                      <div style="color: #856404; font-size: 12px; margin-bottom: 5px;">AS-IS VALUE</div>
                      <div style="color: #856404; font-size: 24px; font-weight: 600;">$${asIsValue?.toLocaleString() || 'TBD'}</div>
                    </div>
                  </td>
                  <td></td>
                  <td valign="top">
                    <div style="background: #fef5e7; border-radius: 6px; padding: 20px; margin-bottom: 10px;">
                      <div style="color: #856404; font-size: 12px; margin-bottom: 5px;">EST. RENOVATION</div>
                      <div style="color: #856404; font-size: 24px; font-weight: 600;">$${Number(renoEstimate || 0).toLocaleString()}</div>
                    </div>
                  </td>
                </tr>
              </table>
              
              <!-- MAO Analysis -->
              <div style="background: #e6fffa; border-left: 4px solid #38b2ac; padding: 15px; margin-top: 20px;">
                <h4 style="margin: 0 0 10px; color: #234e52;">Maximum Allowable Offer (MAO)</h4>
                <table width="100%">
                  <tr>
                    <td width="33%"><strong>MAO @ 20%:</strong> $${mao20pct ? Number(mao20pct).toLocaleString() : 'TBD'}</td>
                    <td width="33%"><strong>MAO @ 15%:</strong> $${mao15pct ? Number(mao15pct).toLocaleString() : 'TBD'}</td>
                    <td width="34%">
                      ${maoGapPct !== null && maoGapPct !== undefined && Number(maoGapPct) < 0 
                        ? `<span style="color: #22543d; font-weight: 600;">✅ ${Math.abs(Number(maoGapPct)).toFixed(0)}% below MAO</span>`
                        : `<span style="color: #c53030; font-weight: 600;">⚠️ ${maoGapPct !== null && maoGapPct !== undefined ? Number(maoGapPct).toFixed(0) : 0}% above MAO</span>`
                      }
                    </td>
                  </tr>
                </table>
              </div>
            </td>
          </tr>
          
          <!-- Conservative Analysis -->
          <tr>
            <td style="padding: 40px 30px; border-bottom: 1px solid #e2e8f0;">
              <h3 style="margin: 0 0 20px; color: #2d3748; font-size: 22px;">
                🎯 Conservative Analysis ($30K Renovation Budget)
              </h3>
              
              <table width="100%" style="background: #f7fafc; border-radius: 6px; padding: 15px;">
                <tr>
                  <td style="padding: 10px;">
                    <div style="margin-bottom: 10px;">
                      <strong>Purchase Price:</strong> $${listPrice.toLocaleString()}<br>
                      <strong>Renovation Budget:</strong> $${conservativeRenovation.toLocaleString()}<br>
                      <strong>Holding Costs (4 mo):</strong> $${holdingCosts.toFixed(0).toLocaleString()}<br>
                      <strong>Selling Costs (6%):</strong> $${sellingCosts.toFixed(0).toLocaleString()}<br>
                      <hr style="border: none; border-top: 1px solid #e2e8f0; margin: 10px 0;">
                      <strong>Total Investment:</strong> $${totalInvestment.toFixed(0).toLocaleString()}
                    </div>
                  </td>
                  <td style="padding: 10px; text-align: center;">
                    <div style="background: #fff; border-radius: 6px; padding: 20px;">
                      <div style="color: #718096; font-size: 12px; margin-bottom: 5px;">CONSERVATIVE ARV</div>
                      <div style="color: #2d3748; font-size: 28px; font-weight: 700;">$${conservativeARV.toLocaleString()}</div>
                      <div style="color: #22543d; font-size: 20px; font-weight: 600; margin-top: 10px;">
                        ${Number(conservativeROI || 0).toFixed(1)}% ROI
                      </div>
                    </div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
          <!-- Comparable Sales Analysis -->
          <tr>
            <td style="padding: 40px 30px; border-bottom: 1px solid #e2e8f0;">
              <h3 style="margin: 0 0 20px; color: #2d3748; font-size: 22px;">
                🏠 Comparable Sales Analysis
              </h3>
              
              <p style="color: #718096; margin-bottom: 20px;">
                Total comparables analyzed: <strong>${totalComps || 0}</strong> properties within market area
              </p>
              
              <!-- Renovated Comps -->
              ${renovatedComps && renovatedComps.length > 0 ? `
              <h4 style="margin: 20px 0 15px; color: #2d3748; font-size: 18px;">
                📈 Renovated Comparables (ARV Support)
              </h4>
              
              <table width="100%" style="border-collapse: collapse;">
                <tr style="background: #f7fafc;">
                  <th style="padding: 10px; text-align: left; border-bottom: 2px solid #e2e8f0;">#</th>
                  <th style="padding: 10px; text-align: left; border-bottom: 2px solid #e2e8f0;">Address</th>
                  <th style="padding: 10px; text-align: right; border-bottom: 2px solid #e2e8f0;">Sold Price</th>
                  <th style="padding: 10px; text-align: center; border-bottom: 2px solid #e2e8f0;">$/sqft</th>
                  <th style="padding: 10px; text-align: center; border-bottom: 2px solid #e2e8f0;">Distance</th>
                </tr>
                ${renovatedComps.slice(0, 5).map((comp, index) => `
                <tr>
                  <td style="padding: 10px; border-bottom: 1px solid #e2e8f0;">${index + 1}</td>
                  <td style="padding: 10px; border-bottom: 1px solid #e2e8f0;">
                    ${comp.address || comp.unparsed_address || 'N/A'}<br>
                    <small style="color: #718096;">
                      ${comp.living_area?.toLocaleString() || '?'} sqft | Sold ${comp.close_date || comp.comp_sold_date || comp.sold_date || 'N/A'}
                    </small>
                  </td>
                  <td style="padding: 10px; text-align: right; border-bottom: 1px solid #e2e8f0; font-weight: 600;">
                    $${(comp.close_price || comp.comp_sold_price || comp.sold_price || comp.sale_price || 0).toLocaleString()}
                  </td>
                  <td style="padding: 10px; text-align: center; border-bottom: 1px solid #e2e8f0;">
                    $${(comp.comp_price_per_sqft || comp.price_per_sqft)?.toFixed(0) || 'N/A'}
                  </td>
                  <td style="padding: 10px; text-align: center; border-bottom: 1px solid #e2e8f0;">
                    ${(comp.comp_distance_miles || comp.distance_miles)?.toFixed(2) || '?'} mi
                  </td>
                </tr>
                `).join('')}
              </table>
              
              <div style="background: #f0fff4; border-radius: 6px; padding: 15px; margin-top: 15px;">
                <strong>Average Renovated Sale Price:</strong> $${calculateAveragePrice(renovatedComps).toLocaleString()}<br>
                <strong>Conservative ARV (Bottom 3):</strong> $${conservativeARV.toLocaleString()}
              </div>
              ` : ''}
              
              <!-- As-Is Comps -->
              ${asIsComps && asIsComps.length > 0 ? `
              <h4 style="margin: 30px 0 15px; color: #2d3748; font-size: 18px;">
                📉 As-Is Comparables (Current Value Support)
              </h4>
              
              <table width="100%" style="border-collapse: collapse;">
                <tr style="background: #f7fafc;">
                  <th style="padding: 10px; text-align: left; border-bottom: 2px solid #e2e8f0;">#</th>
                  <th style="padding: 10px; text-align: left; border-bottom: 2px solid #e2e8f0;">Address</th>
                  <th style="padding: 10px; text-align: right; border-bottom: 2px solid #e2e8f0;">Sold Price</th>
                  <th style="padding: 10px; text-align: center; border-bottom: 2px solid #e2e8f0;">Distance</th>
                </tr>
                ${asIsComps.slice(0, 3).map((comp, index) => `
                <tr>
                  <td style="padding: 10px; border-bottom: 1px solid #e2e8f0;">${index + 1}</td>
                  <td style="padding: 10px; border-bottom: 1px solid #e2e8f0;">
                    ${comp.address || comp.unparsed_address || 'N/A'}<br>
                    <small style="color: #718096;">Sold ${comp.close_date || comp.comp_sold_date || comp.sold_date || 'N/A'}</small>
                  </td>
                  <td style="padding: 10px; text-align: right; border-bottom: 1px solid #e2e8f0; font-weight: 600;">
                    $${(comp.close_price || comp.comp_sold_price || comp.sold_price || comp.sale_price || 0).toLocaleString()}
                  </td>
                  <td style="padding: 10px; text-align: center; border-bottom: 1px solid #e2e8f0;">
                    ${(comp.comp_distance_miles || comp.distance_miles)?.toFixed(2) || '?'} mi
                  </td>
                </tr>
                `).join('')}
              </table>
              ` : ''}
            </td>
          </tr>
          
          <!-- AI Analysis Section -->
          <tr id="ai-analysis-section" style="display: none;">
            <td style="padding: 40px 30px; border-bottom: 1px solid #e2e8f0;">
              <!-- AI content will be inserted here -->
            </td>
          </tr>
          
          <!-- Investment Recommendation -->
          <tr>
            <td style="padding: 40px 30px; border-bottom: 1px solid #e2e8f0;">
              <h3 style="margin: 0 0 20px; color: #2d3748; font-size: 22px;">
                💡 Investment Recommendation
              </h3>
              
              <div style="background: #f7fafc; border-radius: 8px; padding: 20px;">
                ${generateRecommendation(roi, maoGapPct, totalComps, daysOnMarket)}
              </div>
              
              <!-- Next Steps -->
              <h4 style="margin: 30px 0 15px; color: #2d3748; font-size: 18px;">
                📋 Next Steps
              </h4>
              
              <ol style="color: #4a5568; line-height: 1.8;">
                <li>Review the complete investment report for additional photos and details</li>
                <li>Schedule a property viewing within 24-48 hours</li>
                <li>Get contractor estimates to validate renovation budget</li>
                <li>Prepare your offer strategy based on MAO guidelines</li>
                <li>Consider cash offer for stronger negotiation position</li>
              </ol>
            </td>
          </tr>
          
          <!-- CTA Section -->
          <tr>
            <td style="padding: 40px 30px; text-align: center;">
              <p style="margin: 0 0 20px; color: #4a5568; font-size: 16px;">
                Ready to move forward with this opportunity?
              </p>
              
              <a href="${shareLink}" style="display: inline-block; background: #667eea; color: #ffffff; text-decoration: none; padding: 16px 40px; border-radius: 6px; font-size: 16px; font-weight: 600;">
                Access Full Investment Report →
              </a>
              
              <p style="margin: 20px 0 0; color: #a0aec0; font-size: 12px;">
                This link expires in 30 days • Includes photos, maps, and detailed financials
              </p>
            </td>
          </tr>
          
          <!-- Agent Contact -->
          ${agentName ? `
          <tr>
            <td style="background: #f7fafc; padding: 30px; text-align: center;">
              <p style="margin: 0 0 10px; color: #718096;">Have questions about this analysis?</p>
              <p style="margin: 0; color: #2d3748; font-size: 18px; font-weight: 600;">${agentName}</p>
              ${agentPhone ? `<p style="margin: 5px 0; color: #4a5568;">📱 ${agentPhone}</p>` : ''}
              ${agentEmail ? `<p style="margin: 5px 0; color: #4a5568;">✉️ ${agentEmail}</p>` : ''}
            </td>
          </tr>
          ` : ''}
          
          <!-- Footer -->
          <tr>
            <td style="background-color: #2d3748; padding: 30px; text-align: center;">
              <p style="margin: 0 0 10px; color: #ffffff; font-size: 14px;">
                Powered by KairosIQ - Professional Real Estate Investment Analysis
              </p>
              <p style="margin: 0; color: #a0aec0; font-size: 12px;">
                This analysis is based on current market data and comparable sales. All projections are estimates.
              </p>
            </td>
          </tr>
          
        </table>
        
      </td>
    </tr>
  </table>
  
</body>
</html>
  `.trim();
}

function calculateConservativeARV(comps: CompData[]): number {
  if (!comps || comps.length === 0) return 0;
  
  // Get sale prices and sort
  const prices = comps
    .map(comp => comp.close_price || comp.comp_sold_price || comp.sold_price || comp.sale_price || 0)
    .filter(price => price > 0)
    .sort((a, b) => a - b);
  
  if (prices.length === 0) return 0;
  
  // Take bottom 3 or all if less than 3
  const bottomPrices = prices.slice(0, Math.min(3, prices.length));
  const avgPrice = bottomPrices.reduce((sum, price) => sum + price, 0) / bottomPrices.length;
  
  // Round to nearest 5000
  return Math.round(avgPrice / 5000) * 5000;
}

function calculateAveragePrice(comps: CompData[]): number {
  if (!comps || comps.length === 0) return 0;
  
  const prices = comps
    .map(comp => comp.close_price || comp.comp_sold_price || comp.sold_price || comp.sale_price || 0)
    .filter(price => price > 0);
  
  if (prices.length === 0) return 0;
  
  return Math.round(prices.reduce((sum, price) => sum + price, 0) / prices.length);
}

function generateRecommendation(roi: number, maoGap?: number, totalComps?: number, daysOnMarket?: number): string {
  let recommendation = '';
  
  // ROI assessment
  if (roi >= 25) {
    recommendation = '<p style="margin: 0 0 15px;"><strong style="color: #22543d;">✅ STRONG BUY:</strong> This property offers exceptional returns well above market average.</p>';
  } else if (roi >= 20) {
    recommendation = '<p style="margin: 0 0 15px;"><strong style="color: #276749;">✅ RECOMMENDED:</strong> Solid investment opportunity with strong profit potential.</p>';
  } else if (roi >= 15) {
    recommendation = '<p style="margin: 0 0 15px;"><strong style="color: #d69e2e;">⚠️ MODERATE:</strong> Acceptable returns but negotiate aggressively on price.</p>';
  } else {
    recommendation = '<p style="margin: 0 0 15px;"><strong style="color: #e53e3e;">❌ MARGINAL:</strong> Returns below typical investment thresholds. Proceed with caution.</p>';
  }
  
  // MAO position
  if (maoGap && maoGap < -10) {
    recommendation += '<p style="margin: 0 0 15px;">The property is priced well below MAO, providing good negotiation leverage and safety margin.</p>';
  } else if (maoGap && maoGap > 0) {
    recommendation += '<p style="margin: 0 0 15px;">⚠️ Property is priced above MAO. Significant price reduction needed to achieve target returns.</p>';
  }
  
  // Comp confidence
  if (totalComps && totalComps >= 10) {
    recommendation += '<p style="margin: 0 0 15px;">Strong comparable data with ' + totalComps + ' properties supports the valuation estimates.</p>';
  }
  
  // DOM urgency
  if (daysOnMarket && daysOnMarket < 7) {
    recommendation += '<p style="margin: 0;"><strong>⏰ Time Sensitive:</strong> New listing - act quickly to avoid competition.</p>';
  } else if (daysOnMarket && daysOnMarket > 60) {
    recommendation += '<p style="margin: 0;">Extended market time suggests motivated seller - opportunity for aggressive negotiation.</p>';
  }
  
  return recommendation;
}

/**
 * Generate plain text version for fallback
 */
export function generateCompAnalysisText(data: DealCompAnalysisData): string {
  const conservativeRenovation = 30000;
  const conservativeARV = calculateConservativeARV(data.renovatedComps || []);
  const holdingCosts = Number(data.listPrice || 0) * 0.008 * 4;
  const sellingCosts = conservativeARV * 0.06;
  const totalInvestment = Number(data.listPrice || 0) + conservativeRenovation + holdingCosts + sellingCosts;
  const conservativeProfit = conservativeARV - totalInvestment;
  const conservativeROI = totalInvestment > 0 ? (conservativeProfit / totalInvestment * 100) : 0;
  
  return `
DETAILED INVESTMENT ANALYSIS
${data.propertyAddress}
MLS #${data.mls_id}

EXECUTIVE SUMMARY
Investment Grade: ${data.investmentGrade || 'A'}
ROI Potential: ${Number(data.roi || 0).toFixed(1)}%
Profit Potential: $${Number(data.profit || 0).toLocaleString()}

PROPERTY DETAILS
- ${data.bedrooms || '?'} Bedrooms / ${data.bathrooms || '?'} Bathrooms
- ${data.sqft?.toLocaleString() || '?'} Square Feet
- Built ${data.yearBuilt || 'N/A'}
- Days on Market: ${data.daysOnMarket || 'New'}

FINANCIAL ANALYSIS
List Price: $${Number(data.listPrice || 0).toLocaleString()}
After Repair Value (ARV): $${Number(data.arv || 0).toLocaleString()}
As-Is Value: $${data.asIsValue ? Number(data.asIsValue).toLocaleString() : 'TBD'}
Renovation Estimate: $${Number(data.renoEstimate || 0).toLocaleString()}

Maximum Allowable Offer (MAO):
- MAO @ 20%: $${data.mao20pct ? Number(data.mao20pct).toLocaleString() : 'TBD'}
- MAO @ 15%: $${data.mao15pct ? Number(data.mao15pct).toLocaleString() : 'TBD'}

CONSERVATIVE ANALYSIS ($30K Budget)
Total Investment: $${Number(totalInvestment).toFixed(0).toLocaleString()}
Conservative ARV: $${Number(conservativeARV).toLocaleString()}
Conservative ROI: ${Number(conservativeROI || 0).toFixed(1)}%

COMPARABLE SALES
Total Comparables: ${data.totalComps || 0}
Renovated Comps: ${data.renovatedComps?.length || 0}
As-Is Comps: ${data.asIsComps?.length || 0}

View full analysis with photos and maps:
${data.shareLink}

Best regards,
${data.agentName || 'Your Real Estate Professional'}
  `.trim();
}